import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  TrackingPageHeaderComponent,
  TrackingPageSidebarComponent
} from 'app/core/tracking-page';
import { Observable, Subject } from 'rxjs';
import { filter, first, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { StorageService } from 'app/core';
import { TrackingPageCoreService } from '../../../core/tracking-page/tracking-page-core.service';
import { firstValueOf, isTruthy, patchProductImages } from 'app/helpers';
import {
  OrderCardNumber,
  TrackingPageNumbersFacade
} from '../../store/tracking-page-numbers';
import { TrackingPageFacade } from '../../store/tracking-page/tracking-page.facade';

// TODO: Refactor imports
@Component({
  selector: 'app-tracking-page-numbers',
  templateUrl: './tracking-page-numbers.component.html',
  styleUrls: ['./tracking-page-numbers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrackingPageNumbersComponent implements OnInit, OnDestroy {
  public readonly numbers$: Observable<OrderCardNumber[]>;
  public readonly level$: Observable<number>;
  public readonly showDetails$: Observable<any[]>;
  public readonly showDetailsLoading$: Observable<any[]>;
  public readonly sideBarItems$: Observable<any[]>;
  public readonly sideBarItemsOpened$: Observable<any[]>;
  public readonly showMoreSideBarItems$: Observable<boolean>;
  public readonly moreValue$: Observable<number>;
  public readonly selectedItem$: Observable<any>;
  public readonly selectedTrackingNumber$: Observable<any>;
  public readonly reviewsEnabled$: Observable<boolean>;
  public readonly isLoadMoreVisible$: Observable<boolean>;
  public readonly moreValues$: Observable<number>;
  readonly loading$: Observable<boolean>;
  public readonly checkpoints$: Observable<any[]>;

  public readonly destroyed$ = new Subject();

  mode$ = new Subject();
  showedThemeImport = false;
  numbers: OrderCardNumber[] = [];

  @ViewChild('trackingPageItems', { read: ElementRef })
  trackingPageItems: ElementRef;
  @ViewChild(TrackingPageSidebarComponent, { read: ElementRef })
  sidebar;
  @ViewChild(TrackingPageHeaderComponent, { read: ElementRef })
  header;

  public readonly trackingPage$: any;
  public readonly hash$: any;
  readonly loadMoreTrackingNumbersVisible$: any;
  readonly moreTrackingNumberValues$: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private trackingPageNumbers: TrackingPageNumbersFacade,
    private storageService: StorageService,
    private translate: TranslateService,
    private trackingPage: TrackingPageFacade,
    trackingPageCore: TrackingPageCoreService
  ) {
    this.loading$ = trackingPageNumbers.loading$;
    this.numbers$ = trackingPageNumbers.all$;
    this.showDetails$ = trackingPageNumbers.showDetails$;
    this.showDetailsLoading$ = trackingPageNumbers.showDetailsLoading$;
    this.sideBarItems$ = trackingPageNumbers.sideBarItems$;
    this.sideBarItemsOpened$ = trackingPageNumbers.sideBarItemsOpened$;
    this.selectedTrackingNumber$ = trackingPageNumbers.selectedTrackingNumber$;
    this.reviewsEnabled$ = trackingPage.reviewsEnabled$;
    this.trackingPage$ = trackingPage.trackingPage$.pipe(
      filter(isTruthy),
      tap((trackingPageModel) =>
        trackingPageCore.processTrackingPage(trackingPageModel, false)
      )
    );
    this.hash$ = trackingPage.hash$;
    this.isLoadMoreVisible$ = trackingPageNumbers.isLoadMoreVisible$;
    this.moreValues$ = trackingPageNumbers.moreValues$;
    this.loadMoreTrackingNumbersVisible$ =
      trackingPageNumbers.isLoadMoreTrackingNumbersVisible$;
    this.moreTrackingNumberValues$ =
      trackingPageNumbers.moreTrackingNumberValues$;
    this.checkpoints$ = trackingPageNumbers.checkpoints$;
  }

  async changeLanguage(id: string) {
    const hash = await firstValueOf(this.hash$);
    this.storageService.setItem('selectedLocale', id);
    this.translate.use(id);
    this.trackingPageNumbers.closeAllDetails();
    this.trackingPage.bootstrapTrackingPage(
      this.activatedRoute.snapshot.params.hash ?? hash,
      id
    );
  }

  ngOnInit() {
    this.mode$.next('desktop');
    this.numbers$.pipe(first((numbers) => !!numbers.length)).subscribe(() => {
      window.scrollTo(0, 0);
    });
    this.numbers$
      .pipe(
        withLatestFrom(this.hash$, this.trackingPage.trackingPage$),
        filter(isTruthy),
        takeUntil(this.destroyed$)
      )
      .subscribe(([numbers, hash, trackingPageModel]) => {
        this.numbers = numbers;
        if (hash === 'test') {
          this.numbers = this.numbers.map((number) => ({
            ...number,
            orderItems: patchProductImages(
              trackingPageModel.mainProductsFeed.productItems,
              number.orderItems
            ),
            trackingNumbers: number.trackingNumbers.map((shipment) => ({
              ...shipment,
              items: patchProductImages(
                trackingPageModel.mainProductsFeed.productItems,
                shipment.items
              )
            }))
          }));
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  trackByFn(index, item) {
    return item.id;
  }

  public selectTrackingNumber(id): void {
    this.trackingPageNumbers.selectTrackingNumber(id);
  }

  scrollToSelectedItem(id) {
    this.numbers$.pipe(first()).subscribe((numbers) => {
      if (this.trackingPageItems) {
        return;
      }
      const index = numbers.findIndex((item) => item.id === id);
      let top =
        index > -1
          ? this.getElementTop(
              this.trackingPageItems.nativeElement.children[index]
            )
          : 0;

      if (window.outerWidth < 991) {
        top = top + 10;
      }

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    });
  }

  getElementTop(element) {
    return 0 + element.offsetTop - element.scrollTop + element.clientTop - 30;
  }

  onToggleDetails({ id, open, orderId }) {
    return open
      ? this.trackingPageNumbers.openDetails(id, orderId)
      : this.trackingPageNumbers.closeDetails(id + orderId);
  }

  toggleSideBarItemOpen({ id, open }) {
    return !open
      ? this.trackingPageNumbers.openSidebarItem(id)
      : this.trackingPageNumbers.closeSidebarItem(id);
  }

  updateReview({ id, data }) {
    this.trackingPageNumbers.updateReview({ shipmentId: id, ...data });
  }

  showMoreSidebarItems() {
    this.trackingPageNumbers.showMore();
  }

  loadMoreTrackingNumbers() {
    this.trackingPageNumbers.loadMoreTrackingNumbers();
  }

  changeMode(mode) {
    this.mode$.next(mode);
  }
}
